import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { nanoid } from '@reduxjs/toolkit';

import { sbq, throwOnError } from '../../shared/utils/supabaseQuery';

import type {
  CustomStandardBase,
  CreateCustomStandardInput,
  UpdateCustomStandardInput,
  MoveCustomStandardInput,
  UUID,
} from '@/shared/types/customStandard';

// Helper to map snake_case from DB to our TS types if needed
const mapRow = (r: any): CustomStandardBase => ({
  id: r.id,
  label: r.label,
  parent_id: r.parent_id ?? null,
  path: r.path,
  title: r.title,
  code: r.code ?? null,
  position: r.position,
  standards_warehouse_id: r.standards_warehouse_id ?? null,
  additional_content: r.additional_content ?? null,
  owner_id: r.owner_id,
  created_at: r.created_at,
  updated_at: r.updated_at,
  is_deleted: r.is_deleted ?? false,
});

// Utility to insert an item into an array of rows based on before/after/position
function insertByPosition(
  arr: CustomStandardBase[],
  item: CustomStandardBase,
  opts: { beforeId?: UUID | null; afterId?: UUID | null; newPosition?: number | null }
) {
  const copy = arr.slice();
  if (opts.afterId) {
    const idx = copy.findIndex((x) => x.id === opts.afterId);
    copy.splice(idx >= 0 ? idx + 1 : copy.length, 0, item);
  } else if (opts.beforeId) {
    const idx = copy.findIndex((x) => x.id === opts.beforeId);
    copy.splice(idx >= 0 ? idx : 0, 0, item);
  } else if (typeof opts.newPosition === 'number' && opts.newPosition >= 0) {
    copy.splice(Math.min(opts.newPosition, copy.length), 0, item);
  } else {
    copy.push(item);
  }
  return copy;
}

export const customStandardsApi = createApi({
  reducerPath: 'customStandardsApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['CustomStandard', 'Children'],
  endpoints: (builder) => ({
    // Get all standards for an owner (flat list)
    getAllByOwner: builder.query<CustomStandardBase[], { ownerId: UUID }>({
      queryFn: sbq(async (sb, { ownerId }) => {
        const rowsAny = throwOnError(
          await sb
            .from('custom_standards')
            .select('*')
            .eq('owner_id', ownerId)
            .eq('is_deleted', false)
            .order('position', { ascending: true })
            .order('created_at', { ascending: true })
        ) as any[];
        return rowsAny.map(mapRow);
      }, 'CustomStandards'),
      providesTags: (result) => [
        { type: 'CustomStandard' as const, id: 'LIST' },
        ...(result?.map((r) => ({ type: 'CustomStandard' as const, id: r.id })) ?? []),
      ],
    }),

    // Get a single node
    getCustomStandardById: builder.query<CustomStandardBase, UUID>({
      queryFn: sbq(async (sb, id) => {
        const row = throwOnError(
          await sb.from('custom_standards').select('*').eq('id', id).single()
        );
        return mapRow(row);
      }, 'CustomStandard'),
      providesTags: (_result, _err, id) => [{ type: 'CustomStandard', id }],
    }),

    // Get children of a parent (or root level when parentId null)
    getChildren: builder.query<CustomStandardBase[], { ownerId: UUID; parentId?: UUID | null }>({
      queryFn: sbq(async (sb, { ownerId, parentId = null }) => {
        const q = sb
          .from('custom_standards')
          .select('*')
          .eq('owner_id', ownerId)
          .eq('is_deleted', false)
          .order('position', { ascending: true })
          .order('created_at', { ascending: true });

        if (parentId === null) q.is('parent_id', null);
        else q.eq('parent_id', parentId);

        const rowsAny = throwOnError(await q) as any[];
        return rowsAny.map(mapRow);
      }, 'CustomStandards'),
      providesTags: (result, _err, { parentId }) => [
        { type: 'Children', id: parentId ?? 'root' },
        ...(result?.map((r) => ({ type: 'CustomStandard' as const, id: r.id })) ?? []),
      ],
    }),

    // Get a full subtree (including the root) using cs_get_subtree
    getSubtree: builder.query<CustomStandardBase[], { rootId: UUID }>({
      queryFn: sbq(async (sb, { rootId }) => {
        const rows = throwOnError(await sb.rpc('cs_get_subtree', { p_root: rootId })) as any[];
        return rows.map(mapRow);
      }, 'CustomStandards'),
      providesTags: (result) =>
        result?.map((r) => ({ type: 'CustomStandard' as const, id: r.id })) ?? [],
    }),

    // Create node via SQL function cs_create_node
    createCustomStandard: builder.mutation<CustomStandardBase, CreateCustomStandardInput>({
      queryFn: sbq(async (sb, p) => {
        const id = throwOnError(
          await sb.rpc('cs_create_node', {
            p_owner: p.ownerId,
            p_title: p.title,
            p_parent: p.parentId ?? null,
            p_code: p.code ?? null,
            p_position: p.position ?? null,
            p_before: p.beforeId ?? null,
            p_after: p.afterId ?? null,
            p_standards_warehouse_id: p.standardsWarehouseId ?? null,
            p_additional: p.additional ?? null,
          })
        ) as UUID;

        const row = throwOnError(
          await sb.from('custom_standards').select('*').eq('id', id).single()
        );
        return mapRow(row);
      }, 'CustomStandard'),
      invalidatesTags: (_r, _e, p) => [
        { type: 'Children', id: p.parentId ?? 'root' },
        { type: 'CustomStandard', id: 'LIST' },
      ],
      async onQueryStarted(p, { dispatch, queryFulfilled }) {
        const tempId = `temp-${nanoid()}` as UUID;
        const nowIso = new Date().toISOString();
        const optimistic: CustomStandardBase = {
          id: tempId,
          label: 0,
          parent_id: p.parentId ?? null,
          path: '',
          title: p.title,
          code: p.code ?? null,
          position: p.position ?? 999999,
          standards_warehouse_id: p.standardsWarehouseId ?? null,
          additional_content: p.additional ?? null,
          owner_id: p.ownerId,
          created_at: nowIso,
          updated_at: nowIso,
          is_deleted: false,
        };

        const patch = dispatch(
          customStandardsApi.util.updateQueryData(
            'getChildren',
            { ownerId: p.ownerId, parentId: p.parentId ?? null },
            (draft) => {
              const inserted = insertByPosition(draft, optimistic, {
                beforeId: p.beforeId ?? null,
                afterId: p.afterId ?? null,
                newPosition: p.position ?? null,
              });
              draft.splice(0, draft.length, ...inserted);
            }
          )
        );

        try {
          const { data } = await queryFulfilled;
          // Replace temp with real
          dispatch(
            customStandardsApi.util.updateQueryData(
              'getChildren',
              { ownerId: p.ownerId, parentId: p.parentId ?? null },
              (draft) => {
                const idx = draft.findIndex((d) => d.id === tempId);
                if (idx >= 0) draft[idx] = data;
              }
            )
          );
        } catch {
          patch.undo();
        }
      },
    }),

    // Update editable fields
    updateCustomStandard: builder.mutation<CustomStandardBase, UpdateCustomStandardInput>({
      queryFn: sbq(async (sb, { id, title, code, standardsWarehouseId, additional }) => {
        const updates: any = {};
        if (title !== undefined) updates.title = title;
        if (code !== undefined) updates.code = code;
        if (standardsWarehouseId !== undefined)
          updates.standards_warehouse_id = standardsWarehouseId;
        if (additional !== undefined) updates.additional_content = additional;

        const row = throwOnError(
          await sb.from('custom_standards').update(updates).eq('id', id).select('*').single()
        );
        return mapRow(row);
      }, 'CustomStandard'),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'CustomStandard', id },
        { type: 'CustomStandard', id: 'LIST' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const patches: any[] = [];

        // Guard: only patch if single-item cache exists
        try {
          patches.push(
            dispatch(
              customStandardsApi.util.updateQueryData('getCustomStandardById', arg.id, (draft) => {
                if (arg.title !== undefined) draft.title = arg.title;
                if (arg.code !== undefined) draft.code = arg.code ?? null;
                if (arg.standardsWarehouseId !== undefined)
                  draft.standards_warehouse_id = arg.standardsWarehouseId ?? null;
                if (arg.additional !== undefined) draft.additional_content = arg.additional ?? null;
                draft.updated_at = new Date().toISOString();
              })
            )
          );
        } catch {
          // not cached, ignore
        }

        // Probe only the keys that actually exist via endpoint selector
        if (arg.ownerId !== undefined) {
          const state: any = getState();
          const selectChildren = customStandardsApi.endpoints.getChildren.select;
          const parents = [arg.parentId ?? null];

          parents.forEach((pid) => {
            const entry = selectChildren({ ownerId: arg.ownerId!, parentId: pid })(state);
            const list = entry?.data as CustomStandardBase[] | undefined;
            if (!Array.isArray(list)) return;
            if (!list.some((d) => d.id === arg.id)) return;

            patches.push(
              dispatch(
                customStandardsApi.util.updateQueryData(
                  'getChildren',
                  { ownerId: arg.ownerId!, parentId: pid },
                  (draft) => {
                    const idx = draft.findIndex((d) => d.id === arg.id);
                    if (idx >= 0) {
                      if (arg.title !== undefined) draft[idx].title = arg.title;
                      if (arg.code !== undefined) draft[idx].code = arg.code ?? null;
                      if (arg.standardsWarehouseId !== undefined)
                        draft[idx].standards_warehouse_id = arg.standardsWarehouseId ?? null;
                      if (arg.additional !== undefined)
                        draft[idx].additional_content = arg.additional ?? null;
                      draft[idx].updated_at = new Date().toISOString();
                    }
                  }
                )
              )
            );
          });
        }

        try {
          const { data } = await queryFulfilled;
          // Sync single-item cache to server result if present
          try {
            dispatch(
              customStandardsApi.util.updateQueryData('getCustomStandardById', data.id, (draft) =>
                Object.assign(draft, data)
              )
            );
          } catch {
            // not cached
          }
        } catch {
          patches.forEach((p) => p.undo?.());
        }
      },
    }),

    // Move / reorder via cs_move_node
    moveCustomStandard: builder.mutation<void, MoveCustomStandardInput>({
      queryFn: sbq(
        async (
          sb,
          { id, newParentId = null, newPosition = null, beforeId = null, afterId = null }
        ) => {
          const { error } = await sb.rpc('cs_move_node', {
            p_id: id,
            p_new_parent: newParentId,
            p_new_position: newPosition,
            p_before: beforeId,
            p_after: afterId,
          });
          if (error) throw error;
          return undefined;
        },
        'CustomStandard'
      ),
      invalidatesTags: (_r, _e, { newParentId, oldParentId }) => [
        { type: 'Children', id: (oldParentId ?? 'root') as any },
        { type: 'Children', id: (newParentId ?? 'root') as any },
        { type: 'CustomStandard', id: 'LIST' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patches: any[] = [];
        const oldPid = arg.oldParentId ?? null;
        const newPid = arg.newParentId ?? null;
        const ownerId = arg.ownerId;

        // Remove from old parent list
        if (ownerId !== undefined) {
          patches.push(
            dispatch(
              customStandardsApi.util.updateQueryData(
                'getChildren',
                { ownerId, parentId: oldPid },
                (draft) => {
                  const idx = draft.findIndex((d) => d.id === arg.id);
                  if (idx >= 0) draft.splice(idx, 1);
                }
              )
            )
          );

          // Insert into new parent list
          patches.push(
            dispatch(
              customStandardsApi.util.updateQueryData(
                'getChildren',
                { ownerId, parentId: newPid },
                (draft) => {
                  const placeholderIndex = draft.findIndex((d) => d.id === arg.id);
                  const placeholder: CustomStandardBase = (
                    placeholderIndex >= 0
                      ? draft[placeholderIndex]
                      : {
                          id: arg.id,
                          label: 0,
                          parent_id: newPid,
                          path: '',
                          title: '',
                          code: null,
                          position:
                            typeof arg.newPosition === 'number' ? arg.newPosition : draft.length,
                          standards_warehouse_id: null,
                          additional_content: null,
                          owner_id: ownerId!,
                          created_at: new Date().toISOString(),
                          updated_at: new Date().toISOString(),
                          is_deleted: false,
                        }
                  ) as CustomStandardBase;
                  const inserted = insertByPosition(
                    draft.filter((d) => d.id !== arg.id),
                    placeholder,
                    {
                      beforeId: arg.beforeId ?? null,
                      afterId: arg.afterId ?? null,
                      newPosition: arg.newPosition ?? null,
                    }
                  );
                  draft.splice(0, draft.length, ...inserted);
                }
              )
            )
          );
        }

        try {
          await queryFulfilled;
        } catch (e) {
          patches.forEach((p) => p.undo && p.undo());
        }
      },
    }),

    // Soft delete via cs_delete_node
    deleteCustomStandard: builder.mutation<
      void,
      { id: UUID; parentId?: UUID | null; hard?: boolean; ownerId?: UUID }
    >({
      queryFn: sbq(async (sb, { id, hard = false }) => {
        const { error } = await sb.rpc('cs_delete_node', {
          p_id: id,
          p_hard: hard,
        });
        if (error) throw error;
        return undefined;
      }, 'CustomStandard'),
      invalidatesTags: (_r, _e, { parentId }) => [
        { type: 'Children', id: parentId ?? 'root' },
        { type: 'CustomStandard', id: 'LIST' },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const ownerId = arg.ownerId;
        let patch: any | undefined;
        if (ownerId !== undefined) {
          patch = dispatch(
            customStandardsApi.util.updateQueryData(
              'getChildren',
              { ownerId, parentId: arg.parentId ?? null },
              (draft) => {
                const idx = draft.findIndex((d) => d.id === arg.id);
                if (idx >= 0) draft.splice(idx, 1);
              }
            )
          );
        }
        try {
          await queryFulfilled;
        } catch {
          patch?.undo?.();
        }
      },
    }),

    // Import one or more Warehouse items into Custom Standards
    importWarehouseStandards: builder.mutation<
      CustomStandardBase[],
      {
        ownerId: UUID;
        targetParentId: UUID | null;
        selections: { kind: 'root' | 'parent' | 'child'; id: number; includeChildren?: boolean }[];
      }
    >({
      queryFn: sbq(async (sb, { ownerId, targetParentId, selections }) => {
        const createdTopLevel: CustomStandardBase[] = [];
        type ImportSelection = {
          kind: 'root' | 'parent' | 'child';
          id: number;
          includeChildren?: boolean;
        };

        const parentsByRootId = new Map<number, { id: number; description: string }[]>();
        const childrenByParentId = new Map<number, { id: number; description: string }[]>();

        async function createCsNode(
          title: string,
          parentId: UUID | null,
          whId: number,
          code?: string | null
        ) {
          const newId = throwOnError(
            await sb.rpc('cs_create_node', {
              p_owner: ownerId,
              p_title: title,
              p_parent: parentId,
              p_code: code ?? null,
              p_position: null,
              p_before: null,
              p_after: null,
              p_standards_warehouse_id: whId,
              p_additional: { fromWarehouse: true, warehouse_id: whId },
            })
          ) as UUID;
          const row = throwOnError(
            await sb.from('custom_standards').select('*').eq('id', newId).single()
          );
          return mapRow(row);
        }

        async function fetchRoot(rid: number) {
          const root = throwOnError(
            await sb.from('standards_warehouse').select('id, title').eq('id', rid).single()
          ) as any;
          return root as { id: number; title: string };
        }
        async function fetchParents(rootId: number) {
          const cached = parentsByRootId.get(rootId);
          if (cached) return cached;

          const parents = throwOnError(
            await sb
              .from('standards_warehouse')
              .select('id, document')
              .contains('parent_ids', [rootId])
              .filter('document->>depth', 'eq', '0')
              .order('document->>position', { ascending: true })
          ) as any[];
          const mapped = parents.map((p) => ({
            id: p.id as number,
            description: p.document.description as string,
          }));
          parentsByRootId.set(rootId, mapped);
          return mapped;
        }
        async function fetchChildren(parentId: number) {
          const cached = childrenByParentId.get(parentId);
          if (cached) return cached;

          const children = throwOnError(
            await sb
              .from('standards_warehouse')
              .select('id, document')
              .contains('parent_ids', [parentId])
              .filter('document->>depth', 'eq', '1')
              .order('document->>position', { ascending: true })
          ) as any[];
          const mapped = children.map((c) => ({
            id: c.id as number,
            description: c.document.description as string,
          }));
          childrenByParentId.set(parentId, mapped);
          return mapped;
        }

        async function normalizeSelections(input: ImportSelection[]) {
          const order: string[] = [];
          const byKey = new Map<string, ImportSelection>();

          for (const sel of input) {
            const key = `${sel.kind}:${sel.id}`;
            const existing = byKey.get(key);
            if (!existing) {
              byKey.set(key, { ...sel });
              order.push(key);
              continue;
            }

            if (sel.includeChildren && !existing.includeChildren) {
              existing.includeChildren = true;
            }
          }

          const uniqueSelections = order
            .map((key) => byKey.get(key))
            .filter((sel): sel is ImportSelection => Boolean(sel));

          const coveredParentIds = new Set<number>();
          const coveredChildIds = new Set<number>();

          for (const sel of uniqueSelections) {
            if (sel.kind !== 'root' || !sel.includeChildren) continue;

            const parents = await fetchParents(sel.id);
            for (const parent of parents) {
              coveredParentIds.add(parent.id);
              const children = await fetchChildren(parent.id);
              for (const child of children) coveredChildIds.add(child.id);
            }
          }

          for (const sel of uniqueSelections) {
            if (sel.kind !== 'parent' || !sel.includeChildren) continue;
            if (coveredParentIds.has(sel.id)) continue;

            const children = await fetchChildren(sel.id);
            for (const child of children) coveredChildIds.add(child.id);
          }

          return uniqueSelections.filter((sel) => {
            if (sel.kind === 'parent' && coveredParentIds.has(sel.id)) return false;
            if (sel.kind === 'child' && coveredChildIds.has(sel.id)) return false;
            return true;
          });
        }

        const normalizedSelections = await normalizeSelections(selections);

        for (const sel of normalizedSelections) {
          if (sel.kind === 'root') {
            const root = await fetchRoot(sel.id);
            const createdRoot = await createCsNode(root.title, targetParentId, root.id, null);
            createdTopLevel.push(createdRoot);

            if (sel.includeChildren) {
              const parents = await fetchParents(root.id);
              for (const p of parents) {
                const createdParent = await createCsNode(
                  p.description,
                  createdRoot.id as UUID,
                  p.id,
                  null
                );
                const children = await fetchChildren(p.id);
                for (const c of children) {
                  await createCsNode(c.description, createdParent.id as UUID, c.id, null);
                }
              }
            }
          } else if (sel.kind === 'parent') {
            // fetch the parent node to get description
            const parentRow = throwOnError(
              await sb.from('standards_warehouse').select('id, document').eq('id', sel.id).single()
            ) as any;
            const createdParent = await createCsNode(
              parentRow.document.description,
              targetParentId,
              parentRow.id,
              null
            );
            createdTopLevel.push(createdParent);
            if (sel.includeChildren) {
              const children = await fetchChildren(parentRow.id);
              for (const c of children) {
                await createCsNode(c.description, createdParent.id as UUID, c.id, null);
              }
            }
          } else if (sel.kind === 'child') {
            const childRow = throwOnError(
              await sb.from('standards_warehouse').select('id, document').eq('id', sel.id).single()
            ) as any;
            const created = await createCsNode(
              childRow.document.description,
              targetParentId,
              childRow.id,
              null
            );
            createdTopLevel.push(created);
          }
        }

        return createdTopLevel;
      }, 'ImportWarehouse'),
      invalidatesTags: (_r, _e, { targetParentId }) => [
        { type: 'Children', id: targetParentId ?? 'root' },
        { type: 'CustomStandard', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetCustomStandardByIdQuery,
  useGetChildrenQuery,
  useGetSubtreeQuery,
  useCreateCustomStandardMutation,
  useUpdateCustomStandardMutation,
  useMoveCustomStandardMutation,
  useDeleteCustomStandardMutation,
  useLazyGetChildrenQuery,
  useImportWarehouseStandardsMutation,
  useGetAllByOwnerQuery,
} = customStandardsApi;
